/*
 * Homepage motion (brief section 19), ported from the approved wireframe's initMotion().
 * GSAP 3.13 + ScrollTrigger + SplitText, loaded on the homepage only.
 * Everything runs inside gsap.matchMedia('(prefers-reduced-motion: no-preference)'), after fonts are
 * ready, and never in the theme editor. Subtle and premium; the Faith in Motion tiles stay aligned.
 */
(() => {
  'use strict';
  const G = window.gsap, ST = window.ScrollTrigger, SPLIT = window.SplitText;
  if (!G || !ST || (window.Shopify && window.Shopify.designMode)) return;
  G.registerPlugin(ST, SPLIT);

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const main = $('#MainContent');

  function init() {
    G.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
      /* 1. Scroll progress bar */
      if ($('[data-progress]')) {
        G.to('[data-progress]', { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });
      }

      /* 2. Intro: header items, then the active slide's subline and button */
      G.timeline({ defaults: { ease: 'power3.out' } })
        .from('.hdr__in > *', { y: -18, opacity: 0, duration: 0.6, stagger: 0.07, clearProps: 'transform,opacity' })
        .from('.slide.is-on .slide__sub, .slide.is-on .slide__ctas', { y: 26, opacity: 0, duration: 0.8, stagger: 0.12, clearProps: 'transform,opacity' }, '-=0.25')
        .from('.hero__ctrl', { opacity: 0, duration: 0.6, clearProps: 'opacity' }, '-=0.4');

      /* 3. Hero parallax: media at 12% of scroll, copy lifts and fades as the hero leaves */
      if ($('.hero')) {
        G.to('.hero .slide__media', { yPercent: 12, ease: 'none', scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true } });
        G.to('.hero .slide__copy', { y: -70, opacity: 0, ease: 'none', scrollTrigger: { trigger: '.hero', start: '35% top', end: '90% top', scrub: true } });
      }

      /* 4. Header hides on scroll down, returns on scroll up (from 240px). Never while a drawer,
            popup or the Shop All dropdown is open. Only the bar moves, never the drawer. */
      const bar = $('[data-header]');
      if (bar) {
        const show = () => G.to(bar, { yPercent: 0, duration: 0.3, ease: 'power2.out', overwrite: true });
        ST.create({
          start: 240,
          end: 'max',
          onLeaveBack: show,
          onUpdate: (self) => {
            if ((window.AmenOverlay && window.AmenOverlay.current) || $('.sub.is-open')) return;
            G.to(bar, { yPercent: self.direction === 1 ? -100 : 0, duration: 0.35, ease: 'power2.out', overwrite: true });
          },
        });
        document.addEventListener('overlay:open', show);
        bar.addEventListener('focusin', show);
      }

      /* 5. Section headings: letters rise out of a mask */
      $$('.sec__head h2, .drop__title, .offer h2', main).forEach((h) => {
        const split = SPLIT.create(h, { type: 'words,chars', mask: 'chars' });
        G.from(split.chars, { yPercent: 115, duration: 0.85, ease: 'power4.out', stagger: 0.022, scrollTrigger: { trigger: h, start: 'top 88%', once: true } });
      });

      /* 6. Product cards: staggered rise as each row enters */
      const cards = $$('.pc', main);
      if (cards.length) {
        G.set(cards, { y: 46, opacity: 0 });
        ST.batch(cards, { start: 'top 92%', once: true, onEnter: (batch) => G.to(batch, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out', stagger: 0.08, overwrite: true, clearProps: 'transform' }) });
      }

      /* 7. Category tiles: clip-path wipe up, name bar slides in, gentle 3D tilt (max 6 degrees) */
      if ($('.cats', main)) {
        G.from('.cats .cat', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.1, ease: 'power4.out', stagger: 0.08, scrollTrigger: { trigger: '.cats', start: 'top 85%', once: true } });
        G.from('.cats .cat__name', { yPercent: 100, duration: 0.7, ease: 'power3.out', stagger: 0.08, delay: 0.35, scrollTrigger: { trigger: '.cats', start: 'top 85%', once: true } });
        if (fine) {
          $$('.cats .cat').forEach((tile) => {
            const rx = G.quickTo(tile, 'rotationX', { duration: 0.5, ease: 'power3' });
            const ry = G.quickTo(tile, 'rotationY', { duration: 0.5, ease: 'power3' });
            tile.addEventListener('pointermove', (e) => {
              const r = tile.getBoundingClientRect();
              ry(((e.clientX - r.left) / r.width - 0.5) * 12);
              rx(-((e.clientY - r.top) / r.height - 0.5) * 12);
            });
            tile.addEventListener('pointerleave', () => { rx(0); ry(0); });
          });
        }
      }

      /* 8. AGTG Drop: shutter reveal, then slow parallax; copy rises in */
      if ($('.drop', main)) {
        G.from('.drop__photo', { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.3, ease: 'power4.inOut', scrollTrigger: { trigger: '.drop', start: 'top 80%', once: true } });
        G.fromTo('.drop__media', { yPercent: -6 }, { yPercent: 6, ease: 'none', scrollTrigger: { trigger: '.drop', start: 'top bottom', end: 'bottom top', scrub: true } });
        G.from('.drop .over > p, .drop .over > .btn', { y: 24, opacity: 0, duration: 0.8, ease: 'power3.out', stagger: 0.12, scrollTrigger: { trigger: '.drop', start: 'top 60%', once: true } });
      }

      /* 9. Statement: lines slide opposite ways (+/-11vw, scrub); outline fills with ember */
      if ($('.statement', main)) {
        $$('[data-shift]').forEach((line) => {
          const dir = Number(line.dataset.shift);
          G.fromTo(line, { x: () => -0.11 * window.innerWidth * dir }, { x: () => 0.11 * window.innerWidth * dir, ease: 'none',
            scrollTrigger: { trigger: '.statement', start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true } });
        });
        G.to('.statement__type .o', { backgroundPosition: '0% 0', ease: 'none', scrollTrigger: { trigger: '.statement', start: 'top 70%', end: 'bottom 40%', scrub: true } });
        G.from('.statement__body > *', { y: 30, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.12, scrollTrigger: { trigger: '.statement__body', start: 'top 88%', once: true } });
      }

      /* 10. Trust row and community grid (grid staggers from the center) */
      if ($('.trust', main)) G.from('.trust p', { y: 20, opacity: 0, duration: 0.7, ease: 'power3.out', stagger: 0.1, scrollTrigger: { trigger: '.trust', start: 'top 90%', once: true } });
      if ($('.ugc', main)) G.from('.ugc a', { scale: 0.86, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: { each: 0.06, from: 'center', grid: 'auto' }, scrollTrigger: { trigger: '.ugc', start: 'top 85%', once: true } });

      /* 11. Faith in motion: staggered wipe up with a slight video scale settle. No parallax:
             the tiles must stay perfectly aligned. */
      if ($('.reels', main)) {
        G.from('.reel__in', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.2, ease: 'power4.inOut', stagger: 0.1, scrollTrigger: { trigger: '.reels', start: 'top 85%', once: true } });
        G.from('.reel__media', { scale: 1.2, duration: 1.6, ease: 'power3.out', stagger: 0.1, scrollTrigger: { trigger: '.reels', start: 'top 85%', once: true } });
      }

      /* Offer band copy */
      if ($('.offer', main)) G.from('.offer .offer__in > div:last-child > *', { y: 26, opacity: 0, duration: 0.8, ease: 'power3.out', stagger: 0.1, scrollTrigger: { trigger: '.offer', start: 'top 80%', once: true } });

      /* 12. Magnetic buttons (pointer-fine only, small offset) */
      if (fine) {
        $$('.hero .btn, .drop .btn, .offer .btn, .sec__head .link', main).forEach((btn) => {
          const x = G.quickTo(btn, 'x', { duration: 0.4, ease: 'power3' });
          const y = G.quickTo(btn, 'y', { duration: 0.4, ease: 'power3' });
          btn.addEventListener('pointermove', (e) => {
            const r = btn.getBoundingClientRect();
            x((e.clientX - r.left - r.width / 2) * 0.25);
            y((e.clientY - r.top - r.height / 2) * 0.35);
          });
          btn.addEventListener('pointerleave', () => { x(0); y(0); });
        });
      }

      return () => { if (bar) G.set(bar, { clearProps: 'transform' }); };
    });

    // Re-measure once images and late content have loaded (ScrollTrigger also refreshes on resize).
    window.addEventListener('load', () => ST.refresh());
  }

  (document.fonts ? document.fonts.ready : Promise.resolve()).then(init);
})();
